import { ChevronDown, Circle, CircleCheck, File as FileIcon, Loader2, X, AlertCircle, UploadCloud, LucideVideo, ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { IconButton } from './icon-button';
import { useUploadController } from '@/hooks/use-upload-controller';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from './confirm-modal';
import { useEffect, useState } from 'react';

/**
 * 上传进度组件
 * @returns 
 */
export const UploadProgress = () => {
  const { t } = useTranslation();
  const {
    tasks,
    isVisible,
    isCollapsed,
    handleClose,
    setIsCollapsed
  } = useUploadController();

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // 计算统计数据
  const pendingCount = tasks.filter(t => t.status === 'pending' || t.status === 'uploading').length;
  const successCount = tasks.filter(t => t.status === 'success').length;
  const isAllFinished = pendingCount === 0 && tasks.length > 0;

  const closeUploadProgress = () => {
    setShowConfirmModal(false);
    handleClose();
  };

  useEffect(() => {
    if (!isVisible) {
      setShowConfirmModal(false);
    }
  }, [isVisible]);

  // 页面关闭保护
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (pendingCount > 0) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [pendingCount, t]);

  if (!isVisible) return null;

  return (
    <div className={cn(
      "fixed bottom-0 right-6 z-[9999] border border-gray-200 shadow-medium rounded-t-2xl select-none transition-all duration-300",
      'w-[360px] bg-white flex flex-col overflow-hidden max-h-[300px]',
    )}>
      <div className='w-full min-h-[55px] pl-4 pr-2 flex items-center justify-between bg-[#F8FAFD] border-b border-gray-100'>
        <div className='text-base font-medium text-gray-700'>
          {isAllFinished ? (
            <span className="text-green-600">
              {t('upload_progress.finished_title', { successCount })}
            </span>
          ) : (
            <span>{t('upload_progress.pending_title', { pendingCount })}</span>
          )}
        </div>
        <div className='flex items-center gap-1'>
          <IconButton
            className='size-9 rounded-full hover:bg-gray-200'
            icon={<ChevronDown className={cn(
              'size-5 transition-transform duration-300 ease-in-out',
              !isCollapsed ? 'rotate-180' : 'rotate-0'
            )} />}
            onClick={() => setIsCollapsed(!isCollapsed)}
          />
          <IconButton
            className='size-9 rounded-full hover:bg-gray-200'
            icon={<X className='size-5' />}
            onClick={() => {
              if (isAllFinished) {
                closeUploadProgress()
              } else {
                setShowConfirmModal(true);
              }
            }}
          />
        </div>
      </div>
      {!isCollapsed && (
        <div className='flex-1 w-full overflow-y-auto min-h-0 bg-white'>
          {tasks.map((task) => (
            <div key={task.taskId} className='w-full flex pl-4 pr-3 py-3 items-center justify-between hover:bg-gray-50 border-b border-gray-50 last:border-0'>
              <div className='flex items-center gap-3 overflow-hidden'>
                <div className='flex-shrink-0 text-gray-500'>
                  {task.status === 'pending' || task.status === 'uploading' ? (
                    <UploadCloud className='size-5' />
                  ) : (
                    task.type.includes('video') ? <LucideVideo className='size-5' />
                      : task.type.includes('image') ? <ImageIcon className='size-5' />
                        : <FileIcon className='size-5' />
                  )}
                </div>
                <div className='flex overflow-hidden'>
                  <div className='text-sm font-medium truncate w-[200px] text-gray-700' title={task.name}>
                    {task.name}
                  </div>
                  {/* 可选：显示具体百分比 */}
                  {/* {task.status === 'uploading' && (
                    <span className='text-xs text-blue-500'>{task.progress}%</span>
                  )} */}
                </div>
              </div>

              <div className='flex-shrink-0 ml-2'>
                {/* 状态图标切换逻辑 */}
                {task.status === 'pending' && (
                  <Circle className='size-6 text-gray-300' /> // 等待中
                )}
                {task.status === 'uploading' && (
                  <Loader2 className='size-6 text-i-primary animate-spin' /> // 上传中
                )}
                {task.status === 'success' && (
                  <CircleCheck className='size-6 text-[#34A853]' /> // 成功
                )}
                {task.status === 'error' && (
                  <AlertCircle className='size-6 text-red-500' /> // 失败
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <ConfirmModal
        open={showConfirmModal}
        title={t('upload_progress.cancel_upload')}
        description={t('upload_progress.cancel_upload_desc')}
        onConfirm={closeUploadProgress}
        onOpenChange={setShowConfirmModal}
      />
    </div>
  );
};